import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: number;
  name: string;
  email: string;
  locale: string;
  timezone: string;
  avatar_path?: string | null;
}

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
}

interface AuthState {
  user: User | null;
  tenants: TenantSummary[];
  currentTenantId: string | null;
  permissions: string[];
  roles: string[];
  enabledModules: string[];

  setSession: (data: {
    user: User;
    tenants: TenantSummary[];
    currentTenantId?: string | null;
    permissions?: string[];
    roles?: string[];
    enabledModules?: string[];
  }) => void;

  switchTenant: (tenantId: string) => void;
  clear: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenants: [],
      currentTenantId: null,
      permissions: [],
      roles: [],
      enabledModules: [],

      setSession: (data) => {
        if (data.currentTenantId) {
          localStorage.setItem('current_tenant_id', data.currentTenantId);
        }
        set({
          user: data.user,
          tenants: data.tenants,
          currentTenantId: data.currentTenantId ?? data.tenants[0]?.id ?? null,
          permissions: data.permissions ?? [],
          roles: data.roles ?? [],
          enabledModules: data.enabledModules ?? [],
        });
      },

      switchTenant: (tenantId) => {
        localStorage.setItem('current_tenant_id', tenantId);
        set({ currentTenantId: tenantId });
      },

      clear: () => {
        localStorage.removeItem('current_tenant_id');
        set({
          user: null,
          tenants: [],
          currentTenantId: null,
          permissions: [],
          roles: [],
          enabledModules: [],
        });
      },
    }),
    { name: 'erp_auth' },
  ),
);

export function useCan(permission: string): boolean {
  return useAuth((s) => s.permissions.includes(permission) || s.roles.includes('super-admin'));
}

export function useModuleEnabled(moduleKey: string): boolean {
  return useAuth((s) => s.enabledModules.includes(moduleKey.toLowerCase()));
}
